import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { open_snack_ac } from '../../actions/snack.ac.js';

/**
 * ITAdmin oversight for soft-deleted DocFlow shells (DF-557 / INC0119984).
 * Lists tombstones from ink.idn_deleted with days remaining until automatic
 * hard-delete, and lets an it-admin restore one within its retention window.
 * Access is gated both here (Access Denied) and on every backend endpoint.
 */
class DeletedShellsLanding extends React.Component {
  state = { rows: [], loading: true, err: null, restoringId: null };

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ loading: true });
    window.sch
      .post('/api/idn/list_deleted_shells', {})
      .then((res) => {
        this.setState({
          rows: _.get(res, 'pkg.records', []),
          loading: false,
          err: null,
        });
      })
      .catch((err) => {
        this.setState({ loading: false, err: err.message || 'load failed' });
      });
  };

  restore = (id) => {
    if (this.state.restoringId) return;
    this.setState({ restoringId: id });
    window.sch
      .post('/api/idn/restore_shell', { _id: id })
      .then(() => {
        this.props.open_snack_ac({
          message: 'Shell restored.',
          variant: 'success',
        });
        this.setState({ restoringId: null });
        this.load();
      })
      .catch((err) => {
        this.props.open_snack_ac({
          message: 'Restore failed: ' + (err.message || 'contact IT'),
          variant: 'error',
        });
        this.setState({ restoringId: null });
      });
  };

  render() {
    const { me } = this.props;
    const roles = _.get(me, 'user.roles', []) || [];
    if (!roles.includes('it-admin')) {
      return (
        <div style={{ padding: 40 }}>
          <Typography variant="h5">Access Denied</Typography>
          <Typography variant="body1">
            The Deleted Shells admin view requires the it-admin role.
          </Typography>
        </div>
      );
    }

    const { rows, loading, err, restoringId } = this.state;

    return (
      <div style={{ padding: 24 }}>
        <Typography variant="h5" gutterBottom>
          Deleted Shells{' '}
          <Button size="small" onClick={this.load} style={{ marginLeft: 8 }}>
            Refresh
          </Button>
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Soft-deleted DocFlow patient shells. Each is permanently removed
          automatically once its retention window elapses; restore returns it to
          live search.
        </Typography>

        {loading && <CircularProgress size={28} style={{ marginTop: 24 }} />}
        {err && (
          <Typography color="error" style={{ marginTop: 16 }}>
            {err}
          </Typography>
        )}

        {!loading && !err && rows.length === 0 && (
          <Typography style={{ marginTop: 24 }}>
            No soft-deleted shells.
          </Typography>
        )}

        {!loading && !err && rows.length > 0 && (
          <Paper style={{ marginTop: 16, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shell (patient)</TableCell>
                  <TableCell>Deleted by</TableCell>
                  <TableCell>Deleted at</TableCell>
                  <TableCell align="right">Days left</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>{r._id}</TableCell>
                    <TableCell>{r.deleted_by}</TableCell>
                    <TableCell>
                      {r.deleted_at
                        ? new Date(r.deleted_at).toLocaleString()
                        : ''}
                    </TableCell>
                    <TableCell align="right">
                      {_.isNil(r.days_remaining) ? '—' : r.days_remaining}
                    </TableCell>
                    <TableCell>{r.reason || ''}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={restoringId === r._id}
                        onClick={() => this.restore(r._id)}
                      >
                        {restoringId === r._id ? (
                          <CircularProgress size={16} />
                        ) : (
                          'Restore'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </div>
    );
  }
}

const mapStateToProps = (stateFromStore) => ({
  me: stateFromStore.me,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ open_snack_ac }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DeletedShellsLanding);
